import { NEW_PERSON_CAPABILITY } from './preload'

// Changing the email of a person's unclaimed BRE account, and replacing a
// record-only person's email (ADR 0057).
//
// BRE Auth's route, on the same Hub channel as placeholders and invites
// (bearer BRE_AUTH_HUB_SECRET):
//   POST /internal/hub/v1/placeholder-email  {sub, email, actorPersonId?, reason?}
//     200 {sub, changed, email, previousEmail, emailVerified: false, placeholder: true, revokedInvitations}
//       changed: false when the account already has that address (a retry);
//       previousEmail is then the address Auth's last change replaced.
//     4xx {error: <code>, message}: see AUTH_PLACEHOLDER_EMAIL_REFUSALS.
//   It sends nothing. The claim invite is a separate, explicit call
//   (AUTH_INVITATIONS_PATH).
export const AUTH_PLACEHOLDER_EMAIL_PATH = '/internal/hub/v1/placeholder-email'

// Auth's refusal codes, and what Hub tells staff for each.
export const AUTH_PLACEHOLDER_EMAIL_REFUSALS: Record<string, string> = {
  not_found: 'BRE Auth has no such account.',
  account_merged: 'Merged into another BRE account.',
  account_deleted: 'This BRE account was deleted.',
  account_claimed: 'Claimed. Use Change email.',
  account_signed_in: 'Someone has signed in to it. They change it after claiming.',
  email_verified: 'Its email is confirmed. They change it after claiming.',
  phone_verified: 'A phone on it is confirmed. They change it after claiming.',
  account_has_merges: 'Other accounts were merged into it. Change it in BRE Auth.',
  not_email_placeholder: 'Set up by phone; no email to replace.',
  not_hub_placeholder: 'Set up by another BRE app. They change it after claiming.',
  not_stand_in_email: 'Not a stand-in. They change it after claiming.',
  email_in_use: 'That email is on another BRE account.',
  reserved_email: 'That address can’t get mail.',
  invalid_email: 'Not a valid email.',
  account_changed: 'The account changed. Reload.',
}

// Either capability New person takes (ADR 0028 §7).
export const ACCOUNT_EMAIL_CAPABILITIES = [NEW_PERSON_CAPABILITY.governance, NEW_PERSON_CAPABILITY.access] as const

// The account as Hub last heard about it from BRE Auth.
//   unclaimed   a placeholder nobody has claimed (the change is possible)
//   claimed     they set a password or signed in with Google
//   unconfirmed someone holds the email but never confirmed it
//   deleted     the account is gone
//   unknown     Hub has no word on it
export type AccountClaimState = 'unclaimed' | 'claimed' | 'unconfirmed' | 'deleted' | 'unknown'

export type AccountEmailStatus = {
  personId: string
  // The person's one live BRE Auth link, or null (a record-only person).
  account: { issuer: string; subject: string; state: AccountClaimState; asOf: string | null } | null
  // The email Hub holds for the account: the contact point that came with
  // it, when Hub knows it.
  email: string | null
  // The ADR 0056 "Placeholder email: …" pinned note, while it stands.
  standInNoteId: string | null
  // Whether Hub can ask BRE Auth here at all (not on dev, ADR 0026).
  available: boolean
  unavailableReason: string | null
  // May the email be changed from Hub (by what Hub knows; Auth decides).
  canChange: boolean
  changeBlockedReason: string | null
  // May the invite be sent (an unclaimed account whose email isn't a
  // stand-in; never for a board member while board notifications are
  // paused, ADR 0052).
  canInvite: boolean
  inviteBlockedReason: string | null
}

export type AccountEmailChangeResult = {
  changed: boolean
  email: string
  previousEmail: string | null
  revokedInvitations: number
  standInNoteRetired: boolean
}

export type RemovedContactPoint = {
  id: string
  kind: 'email' | 'phone' | 'address'
  value: string
  provenance: string
  addedAt: string
  removedAt: string
  removedByName: string | null
  // replaced (ADR 0057), account_email_changed (ADR 0057), removed,
  // account_phone_removed, auth_changed (ADR 0066).
  cause: ContactRemovalCause
  reason: string | null
}

export type ContactRemovalCause = 'replaced' | 'account_email_changed' | 'removed' | 'account_phone_removed' | 'auth_changed'

export const STAND_IN_NOTE_PREFIX = 'Placeholder email: '
